import { Activity } from '@spike/activity-model';
import { ACTIVITY_TITLE_MAP, ActivityIcon } from 'components/Activities/Icon/ActivityIcon';
import { MediaGalleryGrid } from 'components/UI/V3/MediaGalleryGrid/MediaGalleryGrid';
import { Media, MediaViewer } from 'components/UI/V3/MediaViewer/MediaViewer';
import { TimelineItemDivider } from 'components/UI/V3/Timeline/Item/Divider/TimelineItemDivider';
import React from 'react';
import styles from './ActivityMedia.module.scss';

interface ActivityMediaProps {
    activity: Activity;
    /** Renders a divider above the photo gallery (used when the activity already shows meta above). */
    showDivider?: boolean;
    galleryClassName?: string;
    zIndex?: number;
}

export const ActivityMedia: React.FC<ActivityMediaProps> = ({
    activity,
    showDivider = false,
    galleryClassName,
    zIndex
}) => {
    const [isMediaViewerOpen, setIsMediaViewerOpen] = React.useState(false);

    const activityType = activity.type;
    const hasNotes = Boolean(activity.note);

    const media = React.useMemo<Array<Media>>(
        () => [
            ...activity.photos.map(photo => ({
                type: 'image' as const,
                src: photo.url,
                thumbnailUrl: photo.thumbnailUrl
            })),
            ...activity.videos.map(video => ({
                type: 'video' as const,
                src: video.url
            }))
        ],
        [activity.photos, activity.videos]
    );

    const openViewer = React.useCallback(() => setIsMediaViewerOpen(true), []);
    const closeViewer = React.useCallback(() => setIsMediaViewerOpen(false), []);

    if (activity.photos.length === 0 && activity.videos.length === 0) {
        return null;
    }

    return (
        <>
            {activity.photos.length > 0 && (
                <>
                    {showDivider && <TimelineItemDivider />}

                    <MediaGalleryGrid
                        media={activity.photos.map(photo => ({
                            type: 'image',
                            url: photo.thumbnailUrl
                        }))}
                        className={galleryClassName}
                        onClick={openViewer}
                    />
                </>
            )}

            {activity.videos.length > 0 && (
                <MediaGalleryGrid
                    media={activity.videos.map(video => ({
                        type: 'video',
                        url: video.url
                    }))}
                    className={galleryClassName}
                    onClick={openViewer}
                />
            )}

            {isMediaViewerOpen && (
                <MediaViewer
                    media={media}
                    zIndex={zIndex ? zIndex + 100 : undefined}
                    onClose={closeViewer}
                >
                    <div className={styles.mediaViewerHeader}>
                        <ActivityIcon type={activityType} />

                        <div className={styles.mediaViewerHeaderMeta}>
                            <p>
                                <b>{ACTIVITY_TITLE_MAP[activityType] ?? activityType}</b>
                            </p>
                            <p>
                                {activity.date.format('ddd, DD MMM YYYY [at] hh:mma')} · {activity.staffName}
                            </p>
                        </div>
                    </div>

                    {hasNotes && (
                        <div className={styles.mediaViewerNote}>
                            <p>{activity.note}</p>
                        </div>
                    )}
                </MediaViewer>
            )}
        </>
    );
};
